import React, {useMemo} from 'react';
import { JobSummary } from './blocks/JobSummary';
import {
    Candidates,
    Benefits,
    RoleResponsibilities,
    Description,
    JobDetails
} from '@/pages/buddy/job/blocks';
import { Files } from '@/components/file/Files';
import { LLMLog } from '@/pages/buddy/shared/debug/llm_log';
import EditableList from '@/components/editable-list/EditableList';
import { ChatConfig, SidebarChat } from '@/layouts/buddy';
import { JobDetailsData } from '@/pages/buddy/types/JobDetailsData';

interface JobContentProps {
    job: JobDetailsData;
    activePage: string;
    loading: boolean;
    setActivePage: (page: string) => void;
    handleSummaryFetched: (activeJobs: number) => void;
    onCpsUpdateComplete: () => void;
}

const JobContent = ({
                        job,
                        activePage,
                        loading,
                        setActivePage,
                        handleSummaryFetched,
                        onCpsUpdateComplete
                    }: JobContentProps) => {

    const chatConfig: ChatConfig = useMemo(() => ({
        entityType: 'job',
        entityId: job.p_id,
        title: 'Refine This Job Profile',
        instructions: 'Give Buddy your instructions to tailor this job profile to your needs - quickly, clearly, and effortlessly.'
    }), [job.p_id]);

    const renderOverview = () => (
        <>
            {/* ✅ Pass refresh handler to SidebarChat */}
            <SidebarChat config={chatConfig} onUpdateComplete={onCpsUpdateComplete} />

            <JobSummary
                setActivePage={setActivePage}
                onSummaryFetched={handleSummaryFetched}
            />

            <Description
                title="Description"
                description={job.description || ''}
                job_p_id={job.p_id}
            />

            <div className="grid lg:grid-cols-3 gap-5 lg:gap-7.5">
                <div className="lg:col-span-1">
                    <div className="mb-7.5">
                        <EditableList
                            title="Required Skills"
                            items={job.required_skills}
                            entityId={job.p_id}
                            entity="job"
                            endpoint="required-skills"
                        />
                    </div>

                    <div className="mb-7.5">
                        <EditableList
                            title="Frameworks"
                            items={job.frameworks}
                            entityId={job.p_id}
                            entity="job"
                            endpoint="frameworks"
                        />
                    </div>

                    <div className="mb-7.5">
                        <EditableList
                            title="Technologies"
                            items={job.technologies}
                            entityId={job.p_id}
                            entity="job"
                            endpoint="technologies"
                        />
                    </div>

                    <div className="mb-7.5">
                        <EditableList
                            title="Required Languages"
                            items={job.required_languages}
                            entityId={job.p_id}
                            entity="job"
                            endpoint="required-languages"
                        />
                    </div>

                    <div className="mb-7.5">
                        <Files
                            title="Files"
                            items={job.files}
                        />
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <div className="mb-7.5">
                        <JobDetails title="Details" job={job} job_p_id={job.p_id} />
                    </div>

                    <div className="mb-7.5">
                        <RoleResponsibilities
                            title="Role Responsibilities"
                            items={job.role_responsibilities}
                            job_p_id={job.p_id}
                        />
                    </div>

                    <div className="mb-7.5">
                        <Benefits title="Benefits" items={job.benefits} job_p_id={job.p_id} />
                    </div>

                </div>
            </div>

            <LLMLog
                title="LLM Logs (Dev/Testing Only)"
                llmLogs={job.llm_log || []}
            />
        </>
    );

    const renderCandidates = () => (
        <Candidates />
    );

    return (
        <div className="grid grid-cols-1 gap-5 lg:gap-7.5">
            {activePage === 'overview' ? renderOverview() : renderCandidates()}
        </div>
    );
};

export { JobContent };
